'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Product } from 'shared/types/product';
import { Close } from 'components/gruenland/icons/Close';
import { Search } from 'components/gruenland/icons/Search';
import { useFormat } from 'helpers/hooks/useFormat';
import Button from '../button/Button';
import Input from '../input';
import SearchItem from './search-item/SearchItem';

type Props = {
  items: Product[];
  onQueryUpdate?: (query: string) => void;
  onClose: () => void;
};

const SearchModalControlled: React.FC<Props> = ({ items, onQueryUpdate, onClose }) => {
  const router = useRouter();

  const { formatMessage } = useFormat({ name: 'gruenland' });

  const [value, setValue] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      onQueryUpdate?.(e.target.value);
    },
    [onQueryUpdate],
  );

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onClose();
      const params = new URLSearchParams();
      params.set('query', value);
      router.push(`/search?${params}`);
    },
    [router, onClose, value],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex size-full items-center justify-center overflow-y-auto bg-[#7f7f7f]/30"
      onClick={onClose}
    >
      <div
        className="absolute inset-x-3 top-3 max-h-[90vh] overflow-auto rounded-md bg-white p-10 xl:top-20"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute right-2 top-2 p-[3px]">
          <Close />
        </button>
        <form className="flex w-full gap-4" onSubmit={onSubmit}>
          <div className="grow">
            <Input
              type="text"
              value={value}
              onChange={onChange}
              placeholder={formatMessage({ id: 'search.placeholder', defaultMessage: 'Cosa stai cercando?' })}
              inputRef={inputRef}
            />
          </div>
          <Button type="submit" disabled={!value} variant="buttonPrimaryGreen">
            <Search />
          </Button>
        </form>

        <div className="mt-6 grid grid-cols-1 gap-[30px] md:grid-cols-2">
          {items.slice(0, 6).map((item) => (
            <SearchItem key={item.productId} hit={item} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchModalControlled;
